import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Terminal, ArrowRight, Layers, Cpu, GitBranch, Sparkles, Activity } from "lucide-react";
import { Button } from "./ui/button";

const STEPS = [
  {
    step: 1,
    line: 17,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: true, vars: [{ name: "array", value: "list", id: "var-array", highlight: true }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3 }, { index: 1, value: 4 }, { index: 2, value: 5 }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9 }
        ]
      }
    ],
    arrows: [{ fromId: "var-array", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 2,
    line: 5,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: false, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] },
      { id: "bs", name: "BINARYSEARCH", active: true, vars: [{ name: "array", value: "list", id: "var-array-local" }, { name: "x", value: "4" }, { name: "low", value: "0", highlight: true }, { name: "high", value: "6", highlight: true }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3, state: "low" }, { index: 1, value: 4 }, { index: 2, value: 5 }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9, state: "high" }
        ]
      }
    ],
    arrows: [{ fromId: "var-array-local", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 3,
    line: 8,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: false, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] },
      { id: "bs", name: "BINARYSEARCH", active: true, vars: [{ name: "array", value: "list", id: "var-array-local" }, { name: "x", value: "4" }, { name: "low", value: "0" }, { name: "high", value: "6" }, { name: "mid", value: "3", highlight: true }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3, state: "low" }, { index: 1, value: 4 }, { index: 2, value: 5 }, { index: 3, value: 6, state: "mid" },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9, state: "high" }
        ]
      }
    ],
    arrows: [{ fromId: "var-array-local", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 4,
    line: 14,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: false, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] },
      { id: "bs", name: "BINARYSEARCH", active: true, vars: [{ name: "array", value: "list", id: "var-array-local" }, { name: "x", value: "4" }, { name: "low", value: "0" }, { name: "high", value: "2", highlight: true }, { name: "mid", value: "3" }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3, state: "low" }, { index: 1, value: 4 }, { index: 2, value: 5, state: "high" }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9 }
        ]
      }
    ],
    arrows: [{ fromId: "var-array-local", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 5,
    line: 8,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: false, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] },
      { id: "bs", name: "BINARYSEARCH", active: true, vars: [{ name: "array", value: "list", id: "var-array-local" }, { name: "x", value: "4" }, { name: "low", value: "0" }, { name: "high", value: "2" }, { name: "mid", value: "1", highlight: true }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3, state: "low" }, { index: 1, value: 4, state: "mid" }, { index: 2, value: 5, state: "high" }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9 }
        ]
      }
    ],
    arrows: [{ fromId: "var-array-local", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 6,
    line: 10,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: false, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }] },
      { id: "bs", name: "BINARYSEARCH", active: true, vars: [{ name: "array", value: "list", id: "var-array-local" }, { name: "x", value: "4" }, { name: "low", value: "0" }, { name: "high", value: "2" }, { name: "mid", value: "1" }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3, state: "low" }, { index: 1, value: 4, state: "match" }, { index: 2, value: 5, state: "high" }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9 }
        ]
      }
    ],
    arrows: [{ fromId: "var-array-local", toId: "heap-array" }],
    output: null,
    pause: 1500
  },
  {
    step: 7,
    line: 17,
    stack: [
      { id: "global", name: "GLOBAL FRAME", active: true, vars: [{ name: "array", value: "list", id: "var-array" }, { name: "x", value: "4" }, { name: "binarySearch", value: "fn()" }, { name: "result", value: "1", highlight: true }] }
    ],
    heap: [
      { 
        id: "heap-array", type: "list", name: "List[7]", addr: "0x2F1A",
        items: [
          { index: 0, value: 3 }, { index: 1, value: 4 }, { index: 2, value: 5 }, { index: 3, value: 6 },
          { index: 4, value: 7 }, { index: 5, value: 8 }, { index: 6, value: 9 }
        ]
      }
    ],
    arrows: [{ fromId: "var-array", toId: "heap-array" }],
    output: "1",
    pause: 2000
  }
];

const CODE_LINES = [
  { n: "1", code: "array = [3, 4, 5, 6, 7, 8, 9]", color: "text-slate-300" },
  { n: "2", code: "x = 4", color: "text-slate-300" },
  { n: "3", code: "", color: "" },
  { n: "4", code: "def binarySearch(array, x):", color: "text-primary" },
  { n: "5", code: "    low = 0", color: "text-slate-300" },
  { n: "6", code: "    high = len(array) - 1", color: "text-slate-300" },
  { n: "7", code: "", color: "" },
  { n: "8", code: "    while low <= high:", color: "text-accent" },
  { n: "9", code: "        mid = (low + high) // 2", color: "text-slate-300" },
  { n: "10", code: "        if array[mid] == x:", color: "text-accent" },
  { n: "11", code: "            return mid", color: "text-primary" },
  { n: "12", code: "        elif array[mid] < x:", color: "text-accent" },
  { n: "13", code: "            low = mid + 1", color: "text-slate-300" },
  { n: "14", code: "        else:", color: "text-accent" },
  { n: "15", code: "            high = mid - 1", color: "text-slate-300" },
  { n: "16", code: "    return -1", color: "text-primary" },
  { n: "17", code: "", color: "" },
  { n: "18", code: "result = binarySearch(array, x)", color: "text-slate-300" }
];

const SvgArrows = ({ arrows, containerRef, elementsMap }) => {
  const [paths, setPaths] = useState([]);

  useLayoutEffect(() => {
    const updatePaths = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newPaths = arrows.map(arrow => {
        const fromEl = elementsMap.current[arrow.fromId];
        const toEl = elementsMap.current[arrow.toId];
        if (!fromEl || !toEl) return null;

        const fromRect = fromEl.getBoundingClientRect();
        const toRect = toEl.getBoundingClientRect();

        const startX = fromRect.right - containerRect.left;
        const startY = fromRect.top + fromRect.height / 2 - containerRect.top;
        const endX = toRect.left - containerRect.left;
        const endY = toRect.top + toRect.height / 2 - containerRect.top;

        const cp1x = startX + 70;
        const cp1y = startY;
        const cp2x = endX - 70;
        const cp2y = endY;

        return `M ${startX},${startY} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${endX},${endY}`;
      }).filter(Boolean);
      setPaths(newPaths);
    };

    updatePaths();
    const t = setTimeout(updatePaths, 100); // Wait for AnimatePresence
    window.addEventListener('resize', updatePaths);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updatePaths);
    };
  }, [arrows, elementsMap, containerRef]);

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-20">
      <defs>
        <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#00E676" />
        </marker>
      </defs>
      {paths.map((p, i) => (
        <motion.path
          key={i}
          d={p}
          fill="none"
          stroke="#00E676"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      ))}
    </svg>
  );
};

const CodeSimulatorSection = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const containerRef = useRef(null);
  const elementsMap = useRef({});

  useEffect(() => {
    let timeoutId;
    
    const runSequence = (currentIdx) => {
      const currentStep = STEPS[currentIdx];
      timeoutId = setTimeout(() => {
        const nextIdx = (currentIdx + 1) % STEPS.length;
        setStepIdx(nextIdx);
        runSequence(nextIdx);
      }, currentStep.pause);
    };

    runSequence(0);
    return () => clearTimeout(timeoutId);
  }, []);

  const step = STEPS[stepIdx];

  return (
    <section className="section-base relative py-20 md:py-40 overflow-hidden border-t border-border">
      {/* Background Decor */}
      <div className="card-base absolute inset-x-4 md:inset-x-24 inset-y-10 border overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="flex flex-col xl:grid xl:grid-cols-12 gap-12 xl:gap-16 items-center">
          
          {/* 1. Header Content */}
          <div className="order-2 xl:order-1 xl:col-span-4 flex flex-col gap-8 text-center xl:text-left">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-primary-subtle border-primary-subtle inline-flex items-center gap-3 px-3 py-1.5 border mb-6 mx-auto xl:mx-0">
                <Activity className="w-4 h-4 text-primary" />
                <span className="text-[9px] font-semibold text-primary uppercase tracking-[0.3em]">Live Execution Trace</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-semibold text-white leading-[1] tracking-tighter mb-8 uppercase">
                SEE YOUR<br/>
                <span className="text-primary">CODE</span><br/>
                THINK.
              </h2>
              <p className="font-sora text-muted-foreground text-sm leading-relaxed mb-10 max-w-md mx-auto xl:mx-0">
                Visualize memory, variables, and the call stack in real-time. No setup, no debuggers—just pure understanding.
              </p>
              
              <Link to="/playground" className="w-full sm:w-auto block">
                <Button size="lg" className="w-full sm:w-auto group">
                  Try The Simulator
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* 2. Visual Mockup */}
          <div className="order-1 xl:order-2 xl:col-span-8 w-full xl:perspective-[1000px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ 
                opacity: 1, 
                y: 0,
                rotateY: window.innerWidth > 1280 ? -10 : 0,
                rotateX: window.innerWidth > 1280 ? 2 : 0
              }}
              viewport={{ once: true }}
              transition={{ duration: 1, type: "spring" }}
              className="bg-card text-foreground border-[#00E67633] relative w-full border shadow-2xl overflow-hidden"
            >
              {/* Browser bar */}
              <div className="bg-background border-b border-border flex items-center justify-between px-6 py-4">
                <div className="flex gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/20" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20" />
                  <div className="w-2.5 h-2.5 rounded-full bg-primary/20" />
                </div>
                <div className="px-3 py-1 bg-card border border-border text-[9px] text-muted-foreground uppercase tracking-widest font-semibold">
                  main.py · Step {stepIdx + 1}/{STEPS.length}
                </div>
              </div>

              <div className="flex flex-col md:flex-row min-h-[450px]">
                {/* Code Panel */}
                <div className="bg-background border-r border-border w-full md:w-[45%] p-6 font-mono text-[11px] md:text-[12px] leading-relaxed overflow-hidden">
                  {CODE_LINES.map((row, i) => (
                    <div key={i} className={`flex gap-4 px-2 py-1 transition-all ${i === step.line ? 'bg-[#00E67611] border-l-2 border-primary' : 'border-l-2 border-transparent'}`}>
                      <span className="text-muted-foreground w-4 text-right select-none">{row.n}</span>
                      <span className={`${row.color} truncate whitespace-pre`}>{row.code}</span>
                    </div>
                  ))}
                </div>

                {/* Memory Visualizer Panel */}
                <div ref={containerRef} className="w-full md:w-[55%] p-6 relative flex flex-col sm:flex-row gap-6">
                  <SvgArrows arrows={step.arrows} containerRef={containerRef} elementsMap={elementsMap} />

                  {/* Stack Frames */}
                  <div className="flex-1 flex flex-col gap-4 z-10">
                    <span className="text-[10px] text-primary uppercase tracking-[0.2em] font-semibold mb-1">Stack Frames</span>
                    
                    <AnimatePresence mode="popLayout">
                      {step.stack.map(frame => (
                        <motion.div
                          key={frame.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          style={{ borderLeftColor: frame.active ? '#00E676' : '#F59E0B' }}
                          className="bg-background border-l-2 border-border border p-3 shadow-md"
                        >
                          <div className="text-[9px] font-semibold text-slate-400 mb-2 uppercase tracking-wider">{frame.name}</div>
                          <div className="flex flex-col gap-1">
                            {frame.vars.map(v => (
                              <div 
                                key={v.name} 
                                ref={(el) => { if (v.id) elementsMap.current[v.id] = el; }}
                                style={{ backgroundColor: v.highlight ? '#00E67611' : 'transparent' }}
                                className="flex items-center justify-between px-2 py-1 rounded transition-colors"
                              >
                                <span className="text-[11px] text-white font-semibold">{v.name}</span>
                                <span className="text-[11px] text-accent font-semibold">{v.value}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Heap Objects */}
                  <div className="flex-1 flex flex-col gap-4 z-10 overflow-hidden">
                    <span className="text-[10px] text-primary uppercase tracking-[0.2em] font-semibold mb-1">Heap Objects</span>
                    
                    <AnimatePresence mode="popLayout">
                      {step.heap.map(obj => (
                        <motion.div
                          key={obj.id}
                          ref={(el) => elementsMap.current[obj.id] = el}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="bg-background border-border border p-3 shadow-md relative overflow-x-auto"
                        >
                          <span className="absolute top-2 right-2 text-[8px] text-slate-600 font-semibold">{obj.addr}</span>
                          <div className="text-[9px] font-semibold text-slate-400 mb-2 uppercase tracking-wider">{obj.type}</div>
                          <div className="text-[11px] text-primary font-semibold">{obj.name}</div>
                          
                          {obj.type === 'list' && obj.items && (
                            <div className="flex gap-1 mt-3 pb-1">
                              {obj.items.map((cell) => {
                                let borderColor = 'var(--border)';
                                let bgColor = 'transparent';
                                let pulseClass = '';
                                
                                if (cell.state === 'low' || cell.state === 'high') {
                                  borderColor = '#F59E0B'; // Amber
                                } else if (cell.state === 'mid') {
                                  borderColor = '#00E676'; // Green
                                  bgColor = '#00E67622';
                                } else if (cell.state === 'match') {
                                  borderColor = '#00E676';
                                  bgColor = '#00E67644';
                                  pulseClass = 'animate-pulse';
                                }

                                return (
                                  <div key={cell.index} className="flex flex-col items-center gap-1">
                                    <span className="text-[7px] text-slate-500">{cell.index}</span>
                                    <div 
                                      style={{ borderColor, backgroundColor: bgColor }}
                                      className={`w-7 h-7 flex items-center justify-center border rounded-sm ${pulseClass}`}
                                    >
                                      <span className="text-[10px] text-white font-semibold">{cell.value}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {/* Live Output */}
                    <div className="mt-auto pt-4">
                      <div className="bg-background border-l-[3px] border-l-[#F59E0B] p-3 border border-border">
                        <span className="text-[8px] text-accent uppercase tracking-widest font-semibold block mb-2">Live Output</span>
                        <div className="text-[11px] text-primary min-h-[1.2em]">
                          {step.output ? `▶ ${step.output}` : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CodeSimulatorSection;
